import React, { FC, memo, useEffect, useRef } from 'react';
import { WarehouseForBooking } from 'entities/Warehouse';
import { GeolocationCoords } from 'app/types/common';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { ModernWarehouseCardMobile } from 'features/WarehouseCard/ui/ModernWarehouseCardMobile';

interface ModernWarehousesListMobileProps {
  warehouses: WarehouseForBooking[] | undefined;
  outlinedWarehouseId: Nullable<string>;
  onSelectCard: (lat: number, lng: number, cardId: string) => void;
  onGetRoute: (coords: GeolocationCoords, warehouseId: string) => void;
  onClearRoute: () => void;
  onSelectWarehouse: (warehouse: WarehouseForBooking) => void;
}

export const ModernWarehousesListMobile: FC<ModernWarehousesListMobileProps> = memo((props) => {
  const { warehouses, outlinedWarehouseId, onGetRoute, onClearRoute, onSelectWarehouse } = props;
  const { t } = useAppTranslation('booking');
  const selectedCardRef = useRef<HTMLDivElement>(null);

  // Scroll to selected warehouse when outlinedWarehouseId changes
  useEffect(() => {
    if (outlinedWarehouseId && selectedCardRef.current && warehouses) {
      // Add a small delay to ensure the DOM is updated after sorting
      const timeoutId = setTimeout(() => {
        if (selectedCardRef.current) {
          selectedCardRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);

      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [outlinedWarehouseId, warehouses]);

  return (
    <div className="px-4 py-2">
      <div className="space-y-4">
        {warehouses?.length ? (
          warehouses.map((warehouse) => {
            const isOutlined = outlinedWarehouseId === warehouse.warehouseId;

            const selectCard = (): void => {
              onSelectWarehouse(warehouse);
            };

            // opens the drawer via parent handler

            return (
              <div key={warehouse.warehouseId} ref={isOutlined ? selectedCardRef : null}>
                <ModernWarehouseCardMobile
                  warehouse={warehouse}
                  isOutlined={isOutlined}
                  onGetRoute={onGetRoute}
                  onClearRoute={onClearRoute}
                  onSelectCard={selectCard}
                />
              </div>
            );
          })
        ) : (
          <div className="text-center text-gray-500 py-8">{t('No warehouses found')}</div>
        )}
      </div>
    </div>
  );
});
